import { Inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { AnalyticsEvent, AnalyticsService } from '@lsq-frontend/angular-shared/features/analytics';
import { ErrorToastBlacklistService } from '@lsq-frontend/angular-shared/features/error-toast-blacklist-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { SETTINGS } from '@lsq-frontend/angular-shared/settings';
import {
    FastTrackApiRoutes,
    FastTrackRouteQueryParams,
    UserStates
} from '@lsq-frontend/fasttrack-shared/app-constants';
import { AppSettings } from '@lsq-frontend/fasttrack-shared/environment';
import {
    IdentityService,
    RefreshTokensService
} from '@lsq-frontend/fasttrack-shared/features/identity-service';
import { UserStateService } from '@lsq-frontend/fasttrack-shared/features/user-state-service';
import {
    atlas,
    LOGIN,
    LOGOUT,
    SELECT_ACCOUNT,
    SUPPLY_CHAIN_DIRECT_SELLER
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { GetVendorPortalEnrollmentStatusRequest } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import {
    CompleteEnrollmentRequest,
    CompleteEnrollmentResponse,
    CompleteEnrollmentService
} from '@lsq-frontend/remote-services/all-java/financing-mk1-services';
import {
    CreateVendorPortalSignedTermsOfServiceResponse,
    EnrollmentStatus,
    GetVendorPortalEnrollmentByTokenRequest,
    ResponseCode,
    TransferVendorPortalEnrollmentRequest,
    UserStateStatus,
    UserStatus,
    VendorPortalEnrollmentDetails
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    CompleteFinancingEnrollmentRequest,
    CompleteFinancingEnrollmentService,
    CreateVendorPortalSignedTermsOfServiceRequest,
    CreateVendorPortalSignedTermsOfServiceService,
    GetVendorPortalEnrollmentByTokenService,
    GetVendorPortalEnrollmentRequest,
    GetVendorPortalEnrollmentService,
    GetVendorPortalEnrollmentStatusService,
    SendEmailAddressVerificationCodeRequest,
    SendEmailAddressVerificationCodeService,
    SendPhoneNumberVerificationCodeRequest,
    SendPhoneNumberVerificationCodeService,
    TransferVendorPortalEnrollmentService,
    ValidatePhoneNumberVerificationCodeService,
    ValidateVerificationCodeRequest,
    ValidateVerificationCodeService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { MFAType, ignoreNullish } from '@lsq-frontend/common-ui';
import { getRouterLinkById } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { first, map } from 'rxjs/operators';

export enum FlowStep {
    CREATE_ACCOUNT = 'Create Account',
    VERIFY_EMAIL = 'Confirm Your Email',
    TRANSFER_INVITE = 'Transfer Invite',
    MFA = 'Secure Your Account',
    SMS_MFA = 'SMS MFA',
    TOTP_MFA = 'TOTP MFA',
    SUPPLIER_AGREEMENT = 'Supplier Agreement',
    BANK_ACCOUNT = 'Bank Account',
    COMPLETE_ENROLLMENT = 'Complete Enrollment'
}

export const SUPPLIER_AGREEMENT_VERSION = 1;

@Injectable({ providedIn: 'root' })
export class EnrollmentService {
    private _enrollmentDetails: VendorPortalEnrollmentDetails;
    public userPhoneNumberToVerifyForm: FormGroup;
    public readonly sendVerificationFailureCopy = this._i18n(
        "There's a problem processing the request. Please try again later."
    );

    constructor(
        private _createVendorPortalSignedTermsOfServiceService: CreateVendorPortalSignedTermsOfServiceService,
        private _getVendorPortalEnrollmentByTokenService: GetVendorPortalEnrollmentByTokenService,
        private _getVendorPortalEnrollmentService: GetVendorPortalEnrollmentService,
        private _completeFinancingEnrollmentService: CompleteFinancingEnrollmentService,
        private _sendEmailAddressVerificationCodeService: SendEmailAddressVerificationCodeService,
        private _sendPhoneNumberVerificationCodeService: SendPhoneNumberVerificationCodeService,
        private _validateEmailVerificationCodeService: ValidateVerificationCodeService,
        private _validatePhoneNumberVerificationCodeService: ValidatePhoneNumberVerificationCodeService,
        private _transferVendorPortalEnrollmentService: TransferVendorPortalEnrollmentService,
        private _userService: UserService,
        private _userStateService: UserStateService,
        private _refreshTokensService: RefreshTokensService,
        private _router: Router,
        @Inject(SETTINGS) private _settings: AppSettings,
        private _errorToastBlacklistService: ErrorToastBlacklistService,
        private _i18n: I18n,
        private _alertService: AlertService,
        private _completeEnrollmentService: CompleteEnrollmentService,
        private _getVendorPortalEnrollmentStatusService: GetVendorPortalEnrollmentStatusService,
        private _identityService: IdentityService,
        private _analyticsService: AnalyticsService
    ) {
        this._userService.user$.pipe(ignoreNullish(), first()).subscribe((user) => {
            this.userPhoneNumberToVerifyForm = new FormGroup({
                phoneNumber: new FormControl(user.userPhoneNumber, [Validators.required])
            });
        });
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            this._getVendorPortalEnrollmentByTokenService.endpointUrl,
            ResponseCode.InvalidRequest
        );
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            this._getVendorPortalEnrollmentService.endpointUrl,
            ResponseCode.InvalidRequest
        );

        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.VerifyTotpMfaToken}`,
            ResponseCode.InvalidRequest
        );
    }

    public async getEnrollmentDetails(token: string): Promise<VendorPortalEnrollmentDetails> {
        const request = new GetVendorPortalEnrollmentByTokenRequest({ token });
        const response = await this._getVendorPortalEnrollmentByTokenService.executeOnce(request);
        return response.vendorPortalEnrollmentDetails;
    }

    public async getAndCacheEnrollmentDetailsOrGoToLogin(
        token?: string
    ): Promise<VendorPortalEnrollmentDetails> {
        let vendorPortalEnrollmentDetails: VendorPortalEnrollmentDetails | undefined;
        if (token) {
            const request = new GetVendorPortalEnrollmentByTokenRequest({ token });
            const response = await this._getVendorPortalEnrollmentByTokenService.executeOnce(
                request
            );
            const thisEnrollmentHasBeenCompleted = response.sellerHasEnrolled;
            vendorPortalEnrollmentDetails = response.vendorPortalEnrollmentDetails;

            const userIsLoggedIn = await this._userService.isLoggedIn$.pipe(first()).toPromise();
            const user = await this._userService.user$.pipe(first()).toPromise();
            // See if we need to redirect the user home or to `/login`.
            if (thisEnrollmentHasBeenCompleted) {
                if (userIsLoggedIn) {
                    const hasMultipleCompaniesOrANetworkRequest = await this._identityService.hasMultipleCompaniesOrANetworkRequest$
                        .pipe(first())
                        .toPromise();
                    if (hasMultipleCompaniesOrANetworkRequest) {
                        // Assist in debugging an issue where we are getting redirected to /login.
                        this._analyticsService.triggerEventWithPayload(
                            AnalyticsEvent.EnrollmentRedirectLogin,
                            {
                                redirectUrl: getRouterLinkById(atlas, SELECT_ACCOUNT),
                                context:
                                    'Enrollment completed already. User is logged in and has multiple companies or network request.'
                            }
                        );
                        this._router.navigate(getRouterLinkById(atlas, SELECT_ACCOUNT));
                    } else {
                        // Assist in debugging an issue where we are getting redirected to /login.
                        this._analyticsService.triggerEventWithPayload(
                            AnalyticsEvent.EnrollmentRedirectLogin,
                            {
                                redirectUrl: getRouterLinkById(atlas, SUPPLY_CHAIN_DIRECT_SELLER),
                                context:
                                    'Enrollment completed already. User is logged in and does not have multiple companies or network request.'
                            }
                        );
                        this._router.navigate(getRouterLinkById(atlas, SUPPLY_CHAIN_DIRECT_SELLER));
                    }
                } else {
                    // Assist in debugging an issue where we are getting redirected to /login.
                    this._analyticsService.triggerEventWithPayload(
                        AnalyticsEvent.EnrollmentRedirectLogin,
                        {
                            redirectUrl: getRouterLinkById(atlas, LOGIN),
                            queryParams: { [FastTrackRouteQueryParams.IsEnrolled]: true },
                            context: 'Enrollment completed already. User is not logged in.'
                        }
                    );
                    this._router.navigate(getRouterLinkById(atlas, LOGIN), {
                        queryParams: { [FastTrackRouteQueryParams.IsEnrolled]: true }
                    });
                }
            } else if (await this._anyEnrollmentHasBeenCompletedByThisUser(token)) {
                if (userIsLoggedIn) {
                    // Assist in debugging an issue where we are getting redirected to /login.
                    this._analyticsService.triggerEventWithPayload(
                        AnalyticsEvent.EnrollmentRedirectLogin,
                        {
                            redirectUrl: getRouterLinkById(atlas, SELECT_ACCOUNT),
                            queryParams: {
                                [FastTrackRouteQueryParams.SellerEnrollmentToken]: token
                            },
                            context:
                                'Enrollment completed by this user with another token. User is logged in.'
                        }
                    );
                    this._router.navigate(getRouterLinkById(atlas, SELECT_ACCOUNT), {
                        queryParams: { [FastTrackRouteQueryParams.SellerEnrollmentToken]: token }
                    });
                } else {
                    // Assist in debugging an issue where we are getting redirected to /login.
                    this._analyticsService.triggerEventWithPayload(
                        AnalyticsEvent.EnrollmentRedirectLogin,
                        {
                            redirectUrl: getRouterLinkById(atlas, LOGIN),
                            queryParams: { [FastTrackRouteQueryParams.HasNewNetworkRequest]: true },
                            context:
                                'Enrollment completed by this user with another token. User is not logged in.'
                        }
                    );
                    this._router.navigate(getRouterLinkById(atlas, LOGIN), {
                        queryParams: { [FastTrackRouteQueryParams.HasNewNetworkRequest]: true }
                    });
                }
                // This is to handle users when trying to enroll while signed
                // into another company, which can happen with multiple invitations
            } else if (
                token &&
                user?.currentCompany?.companyKey &&
                user?.currentCompany?.companyKey !== vendorPortalEnrollmentDetails.companyKey
            ) {
                if (
                    userIsLoggedIn &&
                    vendorPortalEnrollmentDetails.enrollmentStatus !== EnrollmentStatus.Invited
                ) {
                    //if the enrollment is past the invited status, means enrollment account has already been created
                    this._analyticsService.triggerEventWithPayload(
                        AnalyticsEvent.EnrollmentRedirectLogin,
                        {
                            redirectUrl: getRouterLinkById(atlas, LOGIN),
                            context: 'User logged in as another company.'
                        }
                    );
                    this._router.navigate(getRouterLinkById(atlas, LOGIN));
                    throw new Error('Current user does not match enrollment token.');
                }
            }
        } else {
            const request = new GetVendorPortalEnrollmentRequest();
            const response = await this._getVendorPortalEnrollmentService.executeOnce(request);
            vendorPortalEnrollmentDetails = response.vendorPortalEnrollmentDetails;
        }

        this._enrollmentDetails = vendorPortalEnrollmentDetails;
        const enrollmentStatus = vendorPortalEnrollmentDetails?.enrollmentStatus;

        if (
            enrollmentStatus !== EnrollmentStatus.Invited &&
            enrollmentStatus !== EnrollmentStatus.Enrolled &&
            enrollmentStatus !== EnrollmentStatus.UserCreated
        ) {
            throw new Error(
                'This VendorPortalEnrollment has a status other than INVITED, USER_CREATED or ENROLLED. Details: ' +
                    vendorPortalEnrollmentDetails
            );
        }
        return vendorPortalEnrollmentDetails;
    }

    public getCachedEnrollmentDetails(): VendorPortalEnrollmentDetails {
        return this._enrollmentDetails;
    }

    public async acceptSupplierAgreement(): Promise<void> {
        await this._signTermsOfService();
        await this.completeSupplierAgreementStep();
    }

    public async completeSupplierAgreementStep(): Promise<void> {
        await this._userStateService.createOrUpdateUserState(
            UserStates.EnrollmentSupplierAgreement,
            UserStateStatus.Done
        );
        await this._userStateService.createOrUpdateUserState(
            UserStates.EnrollmentBankAccount,
            UserStateStatus.Acknowledged
        );
    }

    public async acceptSubsequentSupplierAgreement(companyKey: string): Promise<void> {
        await this._signTermsOfService(companyKey);
    }

    public async enrollmentIsIncomplete(): Promise<boolean> {
        const userStates = await this._userStateService.userStates$.pipe(first()).toPromise();
        return userStates
            .filter(({ userStateType }) => this._isEnrollmentState(userStateType))
            .some(({ userStateStatus }) => userStateStatus === UserStateStatus.Acknowledged);
    }

    public async enrollmentIsPreIdentityVerification(): Promise<boolean> {
        const userStates = await this._userStateService.userStates$.pipe(first()).toPromise();
        return userStates
            .filter(({ userStateType }) =>
                this._isEnrollmentStatePreIdentityVerification(userStateType)
            )
            .some(({ userStateStatus }) => userStateStatus === UserStateStatus.Acknowledged);
    }

    public async getActiveFlowStep(): Promise<FlowStep> {
        const userIsLoggedIn = await this._userService.isLoggedIn$.pipe(first()).toPromise();
        if (userIsLoggedIn) {
            const userStates = await this._userStateService.userStates$.pipe(first()).toPromise();
            const firstAcknowledgedState = userStates
                .filter(({ userStateType }) => this._isEnrollmentState(userStateType))
                .find(({ userStateStatus }) => userStateStatus === UserStateStatus.Acknowledged);
            switch (firstAcknowledgedState?.userStateType) {
                case UserStates.EnrollmentVerifyEmail:
                    return FlowStep.VERIFY_EMAIL;
                case UserStates.EnrollmentMfa:
                    return FlowStep.MFA;
                case UserStates.EnrollmentSupplierAgreement:
                    return FlowStep.SUPPLIER_AGREEMENT;
                case UserStates.EnrollmentBankAccount:
                    return FlowStep.BANK_ACCOUNT;
                default:
                    return FlowStep.CREATE_ACCOUNT;
            }
        }
        return FlowStep.CREATE_ACCOUNT;
    }

    public async createAccount(
        options: CompleteFinancingEnrollmentRequest & {
            companyKey?: string;
            isNotFirstEnrollment?: boolean;
        }
    ): Promise<void> {
        const _options = this._settings.isDemo
            ? {
                  firstName: 'demo',
                  lastName: 'test',
                  emailAddress: 'financing@lsq.com',
                  password: 'demodemo',
                  phoneNumber: '5555555555',
                  token: '1234567890123456789012345678901234567890123456789012345678904444'
              }
            : {
                  firstName: options.firstName,
                  lastName: options.lastName,
                  emailAddress: options.emailAddress,
                  password: options.password,
                  phoneNumber: options.phoneNumber,
                  token: options.token
              };
        await this._completeFinancingEnrollmentService.executeOnce(_options);
        await this._refreshTokensService.execute();
        await this._userService.getAndEmitCurrentUser();

        // Update the user states, but only if this is the user's first enrollment.
        if (!options.isNotFirstEnrollment) {
            await this._upsertInitialEnrollmentStates();
        }
    }

    public async sendEmailAddressVerification(): Promise<void> {
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.SendEmailAddressVerificationCode}`,
            ResponseCode.Error
        );
        let retryVerification = 0;
        let errorThrown: boolean;
        while (retryVerification < 5) {
            errorThrown = false;
            try {
                await this._sendEmailAddressVerificationCodeService.executeOnce(
                    new SendEmailAddressVerificationCodeRequest()
                );
            } catch (e) {
                errorThrown = true;
                if (retryVerification === 4) {
                    this._alertService.error(this.sendVerificationFailureCopy);
                }
            }
            if (!errorThrown) {
                break;
            }
            retryVerification++;
        }
    }

    public async verifyEmailAddress(verificationCode: string): Promise<void> {
        // Handle invalid verification code errors here, not in the response interceptor.
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.ValidateEmailAddressVerificationCode}`,
            ResponseCode.Error
        );
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.ValidateEmailAddressVerificationCode}`,
            ResponseCode.InvalidRequest
        );
        await this._validateEmailVerificationCodeService.executeOnce(
            new ValidateVerificationCodeRequest({ verificationCode })
        );
        await this._userStateService.createOrUpdateUserState(
            UserStates.EnrollmentVerifyEmail,
            UserStateStatus.Done
        );
        if (await this.isMfaRequired()) {
            await this._userStateService.createOrUpdateUserState(
                UserStates.EnrollmentMfa,
                UserStateStatus.Acknowledged
            );
        } else {
            await this._userStateService.createOrUpdateUserState(
                UserStates.EnrollmentSupplierAgreement,
                UserStateStatus.Acknowledged
            );
        }
    }

    public async isMfaRequired(): Promise<boolean> {
        const userStatus = await this._userService.user$
            .pipe(
                first(),
                map((user) => user?.userStatus)
            )
            .toPromise();
        return userStatus === UserStatus.MultiFactorAuthenticationSetupRequired;
    }

    public async transferEnrollment(options: TransferVendorPortalEnrollmentRequest): Promise<void> {
        await this._transferVendorPortalEnrollmentService.executeOnce(options);
    }

    public async sendPhoneNumberVerification(): Promise<void> {
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.SendPhoneNumberVerificationCode}`,
            ResponseCode.Error
        );
        let retryVerification = 0;
        let errorThrown: boolean;
        while (retryVerification < 5) {
            errorThrown = false;
            try {
                await this._sendPhoneNumberVerificationCodeService.executeOnce(
                    new SendPhoneNumberVerificationCodeRequest({
                        phoneNumber: this.userPhoneNumberToVerifyForm.get('phoneNumber')!.value
                    })
                );
            } catch (e) {
                errorThrown = true;
                if (retryVerification === 4) {
                    this._alertService.error(this.sendVerificationFailureCopy);
                }
            }
            if (!errorThrown) {
                break;
            }
            retryVerification++;
        }
    }

    public async verifyPhoneNumber(verificationCode: string): Promise<void> {
        // Handle invalid verification code errors here, not in the response interceptor.
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.ValidatePhoneNumberVerificationCode}`,
            ResponseCode.Error
        );
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.ValidatePhoneNumberVerificationCode}`,
            ResponseCode.InvalidRequest
        );
        await this._validatePhoneNumberVerificationCodeService.executeOnce(
            new ValidateVerificationCodeRequest({ verificationCode })
        );

        await this.completeMfa();
    }

    public async handleAddedPaymentMethodAndCompleteEnrollment(): Promise<void> {
        await this._completeEnrollment(this._enrollmentDetails.refRelationshipKey);
        await this._userStateService.createOrUpdateUserState(
            UserStates.EnrollmentBankAccount,
            UserStateStatus.Done
        );
        await this._userService.getAndEmitCurrentUser();
    }

    private async _anyEnrollmentHasBeenCompletedByThisUser(
        enrollmentToken: string
    ): Promise<boolean> {
        const {
            hasEnrolledPreviously
        } = await this._getVendorPortalEnrollmentStatusService.executeOnce(
            new GetVendorPortalEnrollmentStatusRequest({ enrollmentToken })
        );

        return hasEnrolledPreviously;
    }

    private _isEnrollmentState(userStateType: UserStates | string): boolean {
        return (
            userStateType === UserStates.EnrollmentVerifyEmail ||
            userStateType === UserStates.EnrollmentMfa ||
            userStateType === UserStates.EnrollmentSupplierAgreement ||
            userStateType === UserStates.EnrollmentBankAccount
        );
    }

    private _isEnrollmentStatePreIdentityVerification(userStateType: UserStates | string): boolean {
        return (
            userStateType === UserStates.EnrollmentVerifyEmail ||
            userStateType === UserStates.EnrollmentMfa
        );
    }

    private async _upsertInitialEnrollmentStates(): Promise<void> {
        // Not using Promise.all because of rate limiting concerns.
        await this._userStateService.createOrUpdateUserState(
            UserStates.EnrollmentVerifyEmail,
            UserStateStatus.Acknowledged
        );
        if (await this.isMfaRequired()) {
            await this._userStateService.createOrUpdateUserState(
                UserStates.EnrollmentMfa,
                UserStateStatus.NotStarted
            );
        }
        if (!this._enrollmentDetails.skipVendorAgreement) {
            await this._userStateService.createOrUpdateUserState(
                UserStates.EnrollmentSupplierAgreement,
                UserStateStatus.NotStarted
            );
        }
        await this._userStateService.createOrUpdateUserState(
            UserStates.EnrollmentBankAccount,
            UserStateStatus.NotStarted
        );
    }

    private async _completeEnrollment(
        relationshipKey: string
    ): Promise<CompleteEnrollmentResponse> {
        const request = new CompleteEnrollmentRequest({ relationshipKey });
        return await this._completeEnrollmentService.executeOnce(request);
    }

    private _signTermsOfService(
        companyKey?: string
    ): Promise<CreateVendorPortalSignedTermsOfServiceResponse> {
        const request = new CreateVendorPortalSignedTermsOfServiceRequest({
            version: SUPPLIER_AGREEMENT_VERSION,
            companyKey
        });
        return this._createVendorPortalSignedTermsOfServiceService.executeOnce(request);
    }

    public async completeMfa(): Promise<void> {
        await this._userStateService.createOrUpdateUserState(
            UserStates.EnrollmentMfa,
            UserStateStatus.Done
        );

        if (this._enrollmentDetails.skipVendorAgreement) {
            await this._userStateService.createOrUpdateUserState(
                UserStates.EnrollmentBankAccount,
                UserStateStatus.Acknowledged
            );
        } else {
            await this._userStateService.createOrUpdateUserState(
                UserStates.EnrollmentSupplierAgreement,
                UserStateStatus.Acknowledged
            );
        }
    }
}
